import React, { ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { IconChevronDown, IconDotsVertical } from '@pentabd/icons';
import Text from '../text/Text';
import { Button } from '../button';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useEventListener from '../../hooks/useEventListener';
import DropdownItem, { DropdownItemProps } from './DropdownItem';

export interface DropdownProps {
  testId?: string;
  buttonType?: 'button' | 'label' | 'icon' | 'customIcon';
  iconStyle?: {
    fill?: string;
    stroke?: string;
  };
  customIcon?: ReactNode;
  labelIcon?: ReactNode;
  buttonLabelName?: string;
  info?: {
    name?: string;
    email?: string;
  };
  listItem?: DropdownItemProps[];
  portal?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link?: any;
  isHideOnClickItem?: boolean;
  dropdownPosition?: 'left' | 'right';
  renderItem?: (item: DropdownItemProps) => React.ReactNode;
}

interface PositionProp {
  bottom?: number;
  height?: number;
  left?: number;
  right?: number;
  top?: number;
  width?: number;
  x?: number;
  y?: number;
}

const Dropdown = ({
  testId,
  buttonType,
  iconStyle,
  customIcon,
  labelIcon,
  buttonLabelName,
  listItem,
  info,
  portal,
  Link,
  isHideOnClickItem,
  dropdownPosition = 'right',
  renderItem,
}: DropdownProps) => {
  const [show, setShow] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [scroll, setScroll] = useState(0);
  const [scrollWin, setScrollWin] = useState(0);
  const [position, setPosition] = useState<PositionProp>({});

  const dropdownFullRef = useRef<HTMLDivElement>();
  const buttonRef = useRef<HTMLDivElement>();
  const dropdownRef = useRef<HTMLSpanElement>();

  const handleClickOutside = () => {
    !portal && setShow(false);
  };

  const handleClickInside = () => {
    if (portal) {
      if (buttonRef.current) {
        setPosition(buttonRef.current?.getBoundingClientRect());
      }
      if (dropdownRef.current) {
        setDropdownWidth(dropdownRef.current?.offsetWidth);
      }

      const winScroll =
        document.body.scrollTop || document.documentElement.scrollTop;

      setScrollWin(winScroll);
    }
    setShow((state) => !state);
  };

  useOnClickOutside(dropdownFullRef, handleClickOutside);

  const handleClickOutsideDropdown = () => {
    if (portal && show) {
      setShow(false);
    }
  };

  useOnClickOutside(dropdownRef, handleClickOutsideDropdown);

  const onScroll = () => {
    if (portal && show) {
      // setScroll(window.scrollY);
      // show && setShow(false);
      const winScroll =
        document.body.scrollTop || document.documentElement.scrollTop;

      const height =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;

      const scrolled = winScroll / height;
      setScroll(scrolled);
    }
  };

  useEventListener('scroll', onScroll);

  const DropdownView = () => {
    let top = 0,
      left = 0;
    if (portal) {
      top = position.top + position.height + scroll + scrollWin;
      left = position.x + position.width - dropdownWidth;
    }

    const dropdownClassName = classNames(
      'position-absolute',
      'dropdown-list',
      'w-max',
      'rounded',
      'bg-white',
      'border',
      'shadow-xl',
      'z-5',
      {
        'visibility-visible': show,
        'visibility-hidden': !show,
        'dropdown-list-icon': buttonType == 'icon',
        'dropdown-list-label': buttonType == 'label',
        'dropdown-list-button': buttonType == 'button',
        'dropdown-list-avatar': buttonType == 'customIcon',
        [`${dropdownPosition}-0`]: dropdownPosition,
      },
    );

    return (
      <span
        className={dropdownClassName}
        role="menu"
        {...(portal && {
          style: {
            top: isNaN(top) ? 0 : top,
            left: isNaN(left) ? 0 : left,
          },
          ref: dropdownRef,
        })}
      >
        {info && (
          <div className="d-flex flex-column py-6 px-8">
            <Text color="title" size="sm" weight="semibold">
              {info.name}
            </Text>
            <Text color="subtitle1" size="sm">
              {info.email}
            </Text>
          </div>
        )}
        {listItem &&
          listItem?.map((item, index) => (
            <div key={index}>
              {!item.hide ? (
                <div
                  {...(isHideOnClickItem
                    ? { onClick: () => setShow(false) }
                    : {})}
                >
                  {renderItem ? (
                    <div
                      className={classNames(
                        'd-flex',
                        'gap-10',
                        'justify-content-between',
                        'pointer',
                        'p-6',
                        'w-100',
                        'border-bottom',
                      )}
                      onClick={item?.onClick}
                    >
                      {renderItem(item)}
                    </div>
                  ) : item?.url ? (
                    <Link
                      to={item.disabled ? '' : item?.url}
                      className="text-decoration-none"
                    >
                      <DropdownItem
                        testId={item.testId}
                        key={item.id}
                        icon={item.icon}
                        name={item.name}
                        commandIcon={item.commandIcon}
                        commandLabel={item.commandLabel}
                        hr={item.hr}
                        isButton={item.isButton}
                        buttonType={item.buttonType}
                        disabled={item.disabled}
                      />
                    </Link>
                  ) : (
                    <DropdownItem
                      testId={item.testId}
                      key={item.id}
                      icon={item.icon}
                      name={item.name}
                      commandIcon={item.commandIcon}
                      commandLabel={item.commandLabel}
                      hr={item.hr}
                      onClick={item.onClick}
                      isButton={item.isButton}
                      buttonType={item.buttonType}
                      disabled={item.disabled}
                    />
                  )}
                </div>
              ) : null}
            </div>
          ))}
      </span>
    );
  };

  return (
    <div
      ref={dropdownFullRef}
      className="position-relative"
      data-testid={testId}
    >
      <div onClick={handleClickInside} {...(portal && { ref: buttonRef })}>
        {buttonType == 'label' && (
          <div className="d-flex align-items-center gap-6 pointer">
            {labelIcon && (
              <span className="menu-item-icon d-flex align-items-center justify-content-center">
                {labelIcon}
              </span>
            )}
            <Text color="title" weight="semibold">
              {buttonLabelName}
            </Text>
            <span className="dropdown-arrow d-flex justify-content-center">
              <IconChevronDown size="20" fill="dark" />
            </span>
          </div>
        )}
        {buttonType == 'button' && (
          <Button
            testId="button-component-test"
            className="dropdown border bg-white px-8 py-4 d-flex gap-4"
            fill="outline"
            type="dark"
          >
            <Text size="sm">{buttonLabelName}</Text>
            <span className="dropdown-arrow d-flex justify-content-center">
              <IconChevronDown size="20" fill="dark" />
            </span>
          </Button>
        )}
        {buttonType == 'icon' && (
          <Button
            className="dropdown border-none bg-inherit p-0"
            type="default"
          >
            <IconDotsVertical
              fill={iconStyle?.fill}
              stroke={iconStyle?.stroke}
              size="20"
            />
          </Button>
        )}
        {buttonType == 'customIcon' && (
          <Button
            className="dropdown border-none bg-inherit p-0"
            type="default"
          >
            {customIcon}
          </Button>
        )}
      </div>
      {!portal && <DropdownView />}
      {portal && createPortal(<DropdownView />, document.body)}
    </div>
  );
};
export default Dropdown;
